import { ComponentProps, ReactElement, createContext, useContext } from 'react';

import type { DialogLazyComponentsType, DialogType } from './DialogManager';
import type { ManagedAxleDialogProps } from './ManagedAxleDialog';

// Kept in a separate file so createContext() is never re-executed during HMR.
// All imports from ./DialogManager are type-only (erased at runtime), so this
// module has no runtime dep on DialogManager and won't be invalidated by HMR
// when dialog files change.

export interface IDialogOptions {
    closeOnNavigationChange?: boolean;
}

export interface IActiveDialog {
    id: string;
    type: DialogType;
    component: () => ReactElement;
    options: IDialogOptions;
}

export interface IDialogContext {
    openDialog: <T extends DialogType>(
        type: T,
        props: Omit<ComponentProps<DialogLazyComponentsType[T]>, keyof ManagedAxleDialogProps>,
    ) => void;
    activeDialogs: IActiveDialog[];
}

export const DialogContext = createContext<IDialogContext | null>(null);

export const useDialog = () => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialog must be used within a DialogContextProvider');
    }
    return context;
};
